import { useState, useEffect, useCallback, useRef } from "react";
import { apiHandler } from "../../../api";
import { endpoint } from "../../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useLocation } from "react-router-dom";

const DEBOUNCE_DELAY = 500; // milliseconds

// --- MODIFIED: Updated Initial State ---
const initialRaiseIssueState = {
  domain: "",
  issue_category: "",
  issue_subcategory: "",
  short_desc: "",
  detailed_desc: "",
  refs: [{ ref_type: "", ref_id: "" }], // Changed to an array
  images: [], // Changed to an array for files
  city: "",
  bap_id: "",
  bap_uri: "",
};

export const useReportedIssues = (type = "issues", options) => {
  const dispatch = useDispatch();
  const { authData, storeId, raiseIssue, attribute } = useSelector(
    (state) => state.loginSlice
  );
  const { addToast } = useToasts();
  const location = useLocation();

  // --- Core state ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [analytics, setAnalytics] = useState({});
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  // --- Filters ---
  const [filters, setFilters] = useState({
    status: "",
    issueId: "",
    issue_level: "",
    issue_domain: "",
    resolution_type: "",
    sla_breach: "",
    created_from: "",
    created_to: "",
    search_by: "",
    search_value: "",
    sort_order: "desc",
  });

  const [filterOptions, setFilterOptions] = useState({
    status: [],
    issue_level: [],
    issue_domain: [],
    resolution_type: [],
    sla_breach: [],
  });

  const urlParamsProcessed = useRef(false);

  const [categoryMapping, setCategoryMapping] = useState([]);

  // --- Pagination ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const debounceTimer = useRef(null);

  // --- Raise Issue Modal State ---
  const [isRaiseModalOpen, setIsRaiseModalOpen] = useState(false);
  const [raiseIssueFormData, setRaiseIssueFormData] = useState(
    initialRaiseIssueState
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDomainPreFilled, setIsDomainPreFilled] = useState(false);

  // --- Modal Handlers ---
  const openRaiseModal = () => setIsRaiseModalOpen(true);

  const closeRaiseModal = () => {
    setIsRaiseModalOpen(false);
    setRaiseIssueFormData(initialRaiseIssueState);
    setIsDomainPreFilled(false);
  };

  useEffect(() => {
    if (raiseIssue) {
      setIsRaiseModalOpen(true);
      const categoryObj = categoryMapping.find(
        (c) => c?.category === attribute?.category
      );
      //console.log("domain:", attribute.domain);
      let domainCode =  "";
      //console.log("FILTER:", filterOptions);
        if (attribute.domain && filterOptions.issue_domain && filterOptions.issue_domain.length > 0) {
          const domainObj = filterOptions.issue_domain.find(
            (d) => d.name === attribute.domain
          );
          domainCode = domainObj ? domainObj.code : "";
          //console.log("Mapped domain code:", domainCode);
        }
        

      setRaiseIssueFormData((prev) => ({
        ...prev,
        domain: domainCode || "",
        issue_category: attribute?.category || "",
        bap_id: attribute.to_subscriber_id || "",
        bap_uri: attribute.to_subscriber_uri || "",
        refs: [
          {
            ref_type: attribute.ref_type || "",
            ref_id: attribute.ref_id || "",
          },
        ],
      }));
      setIsDomainPreFilled(true);
      setSubCategoryOptions(categoryObj ? categoryObj.sub_categories : []);
    }
  }, [filterOptions.issue_domain]);

  useEffect(() => {
    if (attribute?.category) {
      const categoryObj = categoryMapping.find(
        (c) => c?.category === attribute?.category
      );
      categoryObj && setSubCategoryOptions(categoryObj.sub_categories);
    }
  }, [categoryMapping]);

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      let url =
        type === "issues"
          ? `${endpoint.GET_ISSUES}?issue_type=REPORTED`
          : `${endpoint.GET_GRIEVANCES}?issue_level=GRIEVANCE&issue_type=REPORTED`;

      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim() !== "") {
          const cleanValue = value.toString().trim();

          if (key === "search_value" || key === "search_by") return;
          params.append(key, cleanValue);
        }
      });

      if (filters.search_by && filters.search_value) {
        const searchVal = filters.search_value.toString().trim();
        params.append(filters.search_by, searchVal);
      }

      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      if (params.toString()) {
        url += url.includes("?")
          ? `&${params.toString()}`
          : `?${params.toString()}`;
      }

      const result = await apiHandler({
        url,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      if (result?.data?.rows) {
        setData(result.data.rows);
        setTotalCount(result.data.pagination?.total || 0);
      } else {
        setData([]);
        setTotalCount(0);
      }

      {
        setFilterOptions(result.data.filters);
      }
      if (result?.data?.analytics) {
        setAnalytics(result.data.analytics);
      } else {
        setAnalytics({}); // Clear if not present
      }

      if (result?.data?.CategoryMapping) {
        setCategoryMapping(result.data.CategoryMapping);
      }
    } catch (err) {
      //console.error("Error fetching reported issues:", err);
      setData([]);
      setTotalCount(0);
      setAnalytics({});
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage, authData, dispatch, storeId, type]);

  useEffect(() => {
    // Only run once, and only after both categoryMapping and filterOptions are loaded
    if (
      urlParamsProcessed.current ||
      !categoryMapping ||
      categoryMapping.length === 0 ||
      !filterOptions.issue_domain ||
      filterOptions.issue_domain.length === 0
    ) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const shouldOpenModal = params.get("raiseIssue") === "true";
    const attributeString = params.get("attribute");

    if (shouldOpenModal && attributeString) {
      try {
        const attributes = JSON.parse(attributeString);

        // Find the matching category object
        const categoryObj = categoryMapping.find(
          (c) => c.category === attributes.category
        );
        const subCats = categoryObj ? categoryObj.sub_categories : [];

        // Set the subcategory options for the dropdown
        setSubCategoryOptions(subCats);

        // Set the form data
      //console.log("domain1 :", attributes.domain);
      let domainCode =  "";
      //console.log("FILTER 1:", filterOptions);
        if (attributes.domain && filterOptions.issue_domain && filterOptions.issue_domain.length > 0) {
          const domainObj = filterOptions.issue_domain.find(
            (d) => d.name === attributes.domain
          );
          domainCode = domainObj ? domainObj.code : "";
          //console.log("Mapped domain code1 :", domainObj);
        }
        

        setRaiseIssueFormData({
          ...initialRaiseIssueState,
          domain: domainCode || "",
          issue_category: attributes.category || "",
          issue_subcategory: "", // User must select this
          bap_id: attributes.to_subscriber_id || "",
          bap_uri: attributes.to_subscriber_uri || "",
          refs: [
            {
              ref_type: attributes.ref_type || "",
              ref_id: attributes.ref_id || "",
            },
          ],
          city: "", // Not in attributes
          short_desc: "", // Not in attributes
          detailed_desc: "", // Not in attributes
        });

        // Open the modal
        setIsRaiseModalOpen(true);
        setIsDomainPreFilled(true);

        // Mark as processed
        urlParamsProcessed.current = true;
      } catch (error) {
        //console.error("Failed to parse attributes from URL", error);
        addToast("Could not pre-fill issue form from URL.", {
          appearance: "error",
        });
      }
    }
  }, [categoryMapping, location.search, addToast, filterOptions.issue_domain]);

  // --- NEW: Moved from component to hook ---
  const handleDomainChange = useCallback((e) => {
    const newDomain = e.target.value;
    setRaiseIssueFormData((prev) => ({
      ...prev,
      domain: newDomain,
      issue_category: "",
      issue_subcategory: "",
    }));
    setSubCategoryOptions([]);
  }, []); // No deps, it only resets

  // --- NEW: Moved from component to hook ---
  const handleCategoryChange = useCallback(
    (e) => {
      const categoryName = e.target.value;
      const categoryObj = categoryMapping.find(
        (c) => c.category === categoryName
      );
      setRaiseIssueFormData((prev) => ({
        ...prev,
        issue_category: categoryName,
        issue_subcategory: "",
      }));
      setSubCategoryOptions(categoryObj ? categoryObj.sub_categories : []);
    },
    [categoryMapping]
  ); // Depends on categoryMapping

  const handleRaiseIssueSubmit = async () => {
    // 1. VALIDATION
    const requiredFields = [
      "domain",
      "issue_category",
      "issue_subcategory",
      "short_desc",
      "city",
      "bap_id",
      "bap_uri",
    ];

    for (const field of requiredFields) {
      if (!raiseIssueFormData[field]) {
        const fieldName = field
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        addToast(`${fieldName} is required.`, { appearance: "warning" });
        return;
      }
    }

    if (!raiseIssueFormData.refs || raiseIssueFormData.refs.length === 0) {
      addToast("At least one Reference is required.", {
        appearance: "warning",
      });
      return;
    }

    for (const [index, ref] of raiseIssueFormData.refs.entries()) {
      if (!ref.ref_type || !ref.ref_id) {
        addToast(
          `Reference Type and Reference ID are required for Reference ${
            index + 1
          }.`,
          { appearance: "warning" }
        );
        return;
      }
    }

    // 2. FORM DATA ASSEMBLY
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      const {
        refs,
        images,
        category_code, // This was in your original code
        ...corePayload
      } = raiseIssueFormData;

      for (const key in corePayload) {
        if (corePayload[key] !== undefined && corePayload[key] !== null) {
          formData.append(key, corePayload[key]);
        }
      }

      formData.append("refs", JSON.stringify(refs));

      if (images && images.length > 0) {
        images.forEach((file) => {
          if (file instanceof File) {
            formData.append("images", file, file.name);
          }
        });
      }

      // 3. API CALL
      const response = await apiHandler({
        url: endpoint.RAISE_ISSUE,
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        authToken: authData,
        dispatch,
        storeId,
      });

      // 4. RESPONSE HANDLING
      if (response?.data?.result?.success) {
        addToast(response.data.result.message || "Issue raised successfully!", {
          appearance: "success",
        });
        closeRaiseModal();
        getData(); // Refresh the data grid
      } else {
        throw new Error(
          response?.data?.result?.message || "Failed to raise issue."
        );
      }
    } catch (err) {
      //console.error("Error raising issue:", err);
      addToast(err.message || "An error occurred. Please try again.", {
        appearance: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      getData();
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(debounceTimer.current);
  }, [
    filters.search_value,
    filters.search_by,
    currentPage,
    itemsPerPage,
    getData,
  ]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // --- Return everything needed by the component ---
  return {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,

    // Modal controls and form handling
    isRaiseModalOpen,
    openRaiseModal,
    closeRaiseModal,
    raiseIssueFormData,
    setRaiseIssueFormData,
    handleRaiseIssueSubmit,
    isSubmitting,
    categoryMapping,
    analytics,

    // --- NEW: Export modal handlers and state ---
    subCategoryOptions,
    handleDomainChange,
    handleCategoryChange,
    isDomainPreFilled,
  };
};
